/* ApplyTest — Phase 3 Initial Screening (10 min, 25 questions).
 *
 * Mixed types: MCQ + True/False + Order steps + 10 English-writing popups
 * (12s each). Candidate sees NO score — only "Passed, continue to practical
 * demo" or "Thank you, we are unable to proceed with your application at
 * this stage". All metrics live in the ATS dashboard.
 *
 * Strict English UI for popups. Refresh attempts, paste attempts, tab
 * switches, blur events, warnings — all logged to backend for the report.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POPUP_INTERVAL_MIN = 14000;  // ~ every 15s — distributes the 10 popups
const POPUP_INTERVAL_MAX = 17000;  // across the 3-minute test
const POPUP_INTERVAL_FAST = 1800;       // when test is in "popup-only" phase
const POPUP_MAX_CONSECUTIVE = 8;        // never fire more than 8 in a row

export default function ApplyTest() {
  const { sid } = useParams();
  const [search] = useSearchParams();
  const isPreview = search.get("preview") === "1";

  const [payload, setPayload] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});                  // {qid: value}
  const [orderDrafts, setOrderDrafts] = useState({});          // {qid: [keys]}
  const [popupAnswers, setPopupAnswers] = useState({});        // {popid: text}
  const [popupMissed, setPopupMissed] = useState([]);
  const [warnings, setWarnings] = useState(0);
  const [refreshAttempts, setRefreshAttempts] = useState(0);
  const [done, setDone] = useState(false);
  const [verdict, setVerdict] = useState(null);                // 'passed' | 'rejected' | 'error'
  const [trainingToken, setTrainingToken] = useState(null);
  const usedPopupIds = useRef(new Set());
  const consecutivePopupRef = useRef(0);
  const popupTimers = useRef({});
  const qStartedAt = useRef({});                               // {qid: ts}
  const timePerQuestion = useRef({});                          // {qid: seconds}
  const [popup, setPopup] = useState(null);                    // active popup
  const [popupSecs, setPopupSecs] = useState(0);
  const [standardDone, setStandardDone] = useState(false);     // standard 10 finished?

  // Document branding override + refresh-attempt counter
  useEffect(() => {
    document.title = "Initial Assessment";
    // Count refresh attempts using sessionStorage
    const key = `_phase3_refreshes_${sid}`;
    const prev = parseInt(sessionStorage.getItem(key) || "0", 10);
    if (prev > 0) {
      setRefreshAttempts(prev);
      // Persist the bump to backend immediately
      axios.post(`${API}/ats/test/${sid}/event`, { type: "tab_switch", detail: "refresh", stage: "phase3" }).catch(() => {});
    }
    sessionStorage.setItem(key, String(prev + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load payload
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ats/test/${sid}/payload-v3`);
        setPayload(data);
        setSecondsLeft(data.duration_seconds);
        if (data.questions[0]) qStartedAt.current[data.questions[0].id] = Date.now();
      } catch (e) {
        setVerdict("error");
      }
    })();
  }, [sid]);

  const sendEvent = (type, detail = "") => {
    axios.post(`${API}/ats/test/${sid}/event`, { type, detail, stage: "phase3" }).catch(() => {});
  };
  const bumpWarning = useCallback(() => setWarnings((w) => Math.min(3, w + 1)), []);

  const submit = useCallback(async () => {
    if (done) return;
    setDone(true);
    if (isPreview) { setVerdict("preview"); return; }
    // Lock in time-per-question for the last viewed question
    const cur = payload?.questions?.[qIdx];
    if (cur && qStartedAt.current[cur.id]) {
      const elapsed = (Date.now() - qStartedAt.current[cur.id]) / 1000;
      timePerQuestion.current[cur.id] = (timePerQuestion.current[cur.id] || 0) + elapsed;
    }
    try {
      const body = {
        answers,
        popup_answers: popupAnswers,
        popup_missed: popupMissed,
        warnings,
        refresh_attempts: refreshAttempts,
        time_per_question: timePerQuestion.current,
      };
      const { data } = await axios.post(`${API}/ats/test/${sid}/submit-v3`, body);
      setVerdict(data.passed ? "passed" : "rejected");
      setTrainingToken(data.training_token || null);
    } catch (e) {
      setVerdict("error");
    }
  }, [done, isPreview, payload, qIdx, answers, popupAnswers, popupMissed, warnings, refreshAttempts, sid]);

  // ── countdown
  useEffect(() => {
    if (!payload || done) return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(t); submit(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [payload, done, submit]);

  // ── anti-cheat (initial test): silent tracking only.
  // Copy/paste is NOT blocked here — that restriction belongs to the
  // practical demo. We still log tab switches so the recruiter sees them
  // in the report, but we never raise a visible warning to the candidate.
  useEffect(() => {
    if (done || isPreview) return;
    const onVis = () => { if (document.visibilityState === "hidden") sendEvent("tab_switch"); };
    const onBlur = () => sendEvent("blur");
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, isPreview]);

  // ── popup scheduler
  useEffect(() => {
    if (!payload || done) return;
    const remaining = payload.popups.filter((p) => !usedPopupIds.current.has(p.id));
    if (!remaining.length) {
      // No popups remain. If standard is also done, finish the test.
      if (standardDone) { submit(); }
      return;
    }
    // If we've fired 8 in a row, force a 6s cool-down before the next one
    // so the candidate gets a brief pause (still relentless overall).
    const cooldown = consecutivePopupRef.current >= POPUP_MAX_CONSECUTIVE;
    const base = standardDone
      ? (cooldown ? 6000 : POPUP_INTERVAL_FAST + Math.floor(Math.random() * 1200))
      : POPUP_INTERVAL_MIN + Math.floor(Math.random() * (POPUP_INTERVAL_MAX - POPUP_INTERVAL_MIN));
    const handle = setTimeout(() => {
      if (done || popup) return;
      const stillRemaining = payload.popups.filter((p) => !usedPopupIds.current.has(p.id));
      if (!stillRemaining.length) { if (standardDone) submit(); return; }
      const p = stillRemaining[Math.floor(Math.random() * stillRemaining.length)];
      usedPopupIds.current.add(p.id);
      qStartedAt.current[p.id] = Date.now();
      if (cooldown) consecutivePopupRef.current = 0;
      consecutivePopupRef.current += 1;
      setPopup(p);
      setPopupSecs(payload.popup_visible_seconds || 12);
    }, base);
    popupTimers.current.next = handle;
    return () => clearTimeout(handle);
    // We DO want to rerun when standardDone flips or another popup finishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, done, standardDone, Object.keys(popupAnswers).length, popupMissed.length, popup]);

  // ── popup 12s countdown
  useEffect(() => {
    if (!popup) return;
    const t = setInterval(() => {
      setPopupSecs((s) => {
        if (s <= 1) {
          clearInterval(t);
          setPopupMissed((m) => [...m, popup.id]);
          const start = qStartedAt.current[popup.id];
          if (start) timePerQuestion.current[popup.id] = (Date.now() - start) / 1000;
          setPopup(null);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [popup]);

  const submitPopup = (text) => {
    setPopupAnswers((m) => ({ ...m, [popup.id]: text }));
    const start = qStartedAt.current[popup.id];
    if (start) timePerQuestion.current[popup.id] = (Date.now() - start) / 1000;
    setPopup(null);
  };

  const recordAnswerAndNext = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    // record time for this question
    const start = qStartedAt.current[qid];
    if (start) timePerQuestion.current[qid] = ((Date.now() - start) / 1000) + (timePerQuestion.current[qid] || 0);
    const next = qIdx + 1;
    if (next < (payload?.questions?.length || 0)) {
      setQIdx(next);
      const nq = payload.questions[next];
      qStartedAt.current[nq.id] = Date.now();
    } else {
      // Standard 10 are done. If popups remain, enter popup-only phase
      // (do NOT submit). The user keeps answering popups until the 15 are
      // delivered or the timer expires.
      const popupsLeft = (payload?.popups || []).length - usedPopupIds.current.size;
      if (popupsLeft > 0) {
        setStandardDone(true);
        consecutivePopupRef.current = 0; // reset so popup-only burst can begin
      } else {
        submit();
      }
    }
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  const css = {
    wrap: { minHeight: "100vh", background: "#0a0a0a", color: "#fff", padding: 16, fontFamily: "system-ui, sans-serif", direction: "rtl" },
    container: { maxWidth: 900, margin: "0 auto" },
    card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 14, padding: 22, marginBottom: 14 },
    headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: 12, marginBottom: 14 },
    h: { fontSize: 18, fontWeight: 900, color: "#d4af37", marginBottom: 14 },
    pillRow: { display: "flex", gap: 6, flexWrap: "wrap" },
    pill: (active, done) => ({ width: 26, height: 6, borderRadius: 3, background: done ? "#10b981" : active ? "#d4af37" : "#2a2a3a" }),
    input: { width: "100%", padding: "10px 12px", background: "rgba(0,0,0,0.5)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, color: "#fff", fontSize: 14, outline: "none" },
    btn: { padding: "11px 22px", background: "#d4af37", color: "#000", border: "none", borderRadius: 10, fontWeight: 900, cursor: "pointer", fontSize: 13 },
    choiceBtn: { width: "100%", padding: "12px 14px", marginBottom: 8, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.25)", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "right" },
  };

  // ── Result screens — candidate sees ONLY pass/fail, no scores
  if (verdict === "preview") {
    return (
      <div style={css.wrap} data-testid="apply-test-page"><div style={{ maxWidth: 560, margin: "60px auto", ...css.card }}>
        <h2 style={css.h}>اكتملت معاينة الاختبار الأولي</h2>
        <p style={{ fontSize: 14, color: "#cbd5e1", lineHeight: 2 }}>هذه نهاية الاختبار الأولي في وضع المعاينة. تابع لمعاينة الـ Sandbox.</p>
        <a href={`/apply/training/${sid}?preview=1`} style={{ display: "inline-block", marginTop: 16, padding: "11px 22px", background: "#10b981", color: "#fff", borderRadius: 10, fontWeight: 900, textDecoration: "none", fontSize: 13 }} data-testid="preview-next-btn">Continue to Sandbox Preview ⇦</a>
      </div></div>
    );
  }
  if (verdict === "passed") {
    return (
      <div style={css.wrap} data-testid="apply-test-page"><div style={{ maxWidth: 560, margin: "60px auto", ...css.card, direction: "ltr", textAlign: "center" }}>
        <h2 style={{ ...css.h, color: "#10b981", fontSize: 22 }} data-testid="verdict-pass">Great, you may proceed to the practical training demo.</h2>
        <p style={{ fontSize: 13, color: "#cbd5e1", marginTop: 10, lineHeight: 1.8, direction: "rtl" }}>أحسنت، يمكنك الانتقال إلى التجربة العملية.</p>
        <a href={`/apply/training/${trainingToken || sid}`}
           style={{ display: "inline-block", marginTop: 22, padding: "13px 30px", background: "#10b981", color: "#fff", borderRadius: 12, fontWeight: 900, textDecoration: "none", fontSize: 14 }}
           data-testid="goto-training-btn">Start practical demo →</a>
      </div></div>
    );
  }
  if (verdict === "rejected" || verdict === "error") {
    return (
      <div style={css.wrap} data-testid="apply-test-page"><div style={{ maxWidth: 560, margin: "60px auto", ...css.card, direction: "ltr", textAlign: "center" }}>
        <h2 style={{ ...css.h, color: "#fca5a5", fontSize: 19 }} data-testid="verdict-fail">Thank you, we are unable to proceed with your application at this stage.</h2>
        <p style={{ fontSize: 13, color: "#9a9a9f", marginTop: 12, direction: "rtl" }}>نشكر تقديمك، ولن نتمكّن من المتابعة في هذه المرحلة. نتمنى لك التوفيق.</p>
      </div></div>
    );
  }

  if (!payload) return <div style={css.wrap}><div style={{ textAlign: "center", marginTop: 80, color: "#a0a0a0" }}>Loading…</div></div>;

  const q = payload.questions[qIdx];
  const total = payload.questions.length;
  const popupsAnsweredCount = Object.keys(popupAnswers).length + popupMissed.length;
  const popupsTotal = (payload.popups || []).length;

  return (
    <div style={css.wrap} data-testid="apply-test-page">
      <div style={css.container}>
        {isPreview && (
          <div data-testid="preview-banner" style={{ background: "#3b82f6", color: "#fff", padding: "10px 16px", borderRadius: 12, marginBottom: 12, fontWeight: 800, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Admin preview — anti-cheat disabled, no scoring.</span>
            <button onClick={submit} style={{ padding: "5px 12px", background: "#fff", color: "#3b82f6", border: "none", borderRadius: 8, fontWeight: 800, cursor: "pointer", fontSize: 12 }} data-testid="preview-finish">Finish preview ⇦</button>
          </div>
        )}

        {/* Header */}
        <div style={css.headerRow}>
          <div style={{ fontSize: 13, color: "#a0a0a0", fontWeight: 600 }}>الاختبار الأولي · Initial Assessment</div>
          <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 22, fontWeight: 900, color: secondsLeft < 30 ? "#dc2626" : "#d4af37" }} data-testid="test-timer">{mm}:{ss}</div>
          <div style={{ fontSize: 12, color: "#9a9a9f" }}>3 min · 3 دقائق</div>
        </div>

        {/* Progress bar */}
        <div style={{ ...css.pillRow, marginBottom: 16 }} data-testid="progress-bar">
          {payload.questions.map((_, i) => <div key={i} style={css.pill(i === qIdx, i < qIdx)} />)}
        </div>

        {/* Active question OR popup-only interstitial */}
        {!standardDone ? (
          <div style={css.card} data-testid={`question-${q.id}`}>
            <div style={{ fontSize: 12, color: "#a0a0a0", marginBottom: 6 }}>السؤال {qIdx + 1} من {total} · Question {qIdx + 1} of {total}</div>
            <h2 style={{ ...css.h, marginBottom: 6 }}>{q.q_ar}</h2>
            {q.q_en && <p style={{ direction: "ltr", textAlign: "left", color: "#cbd5e1", fontSize: 14, marginBottom: 14, lineHeight: 1.7 }}>{q.q_en}</p>}

            {q.type === "bool" && (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => recordAnswerAndNext(q.id, "true")} style={{ ...css.choiceBtn, flex: 1, background: "rgba(16,185,129,0.15)", textAlign: "center" }} data-testid={`q-${q.id}-true`}>صح · True</button>
                <button onClick={() => recordAnswerAndNext(q.id, "false")} style={{ ...css.choiceBtn, flex: 1, background: "rgba(220,38,38,0.15)", textAlign: "center" }} data-testid={`q-${q.id}-false`}>خطأ · False</button>
              </div>
            )}

            {q.type === "choice" && (
              <div>
                {q.options.map((o) => (
                  <button key={o.k} onClick={() => recordAnswerAndNext(q.id, o.k)} style={css.choiceBtn} data-testid={`q-${q.id}-${o.k}`}>
                    <div>{o.label_ar}</div>
                    {o.label_en && <div style={{ direction: "ltr", textAlign: "left", color: "#9ca3af", fontSize: 12, marginTop: 2, fontWeight: 500 }}>{o.label_en}</div>}
                  </button>
                ))}
              </div>
            )}

            {q.type === "order" && (
              <OrderQuestion
                q={q}
                draft={orderDrafts[q.id] || []}
                setDraft={(arr) => setOrderDrafts((d) => ({ ...d, [q.id]: arr }))}
                onSubmit={(arr) => recordAnswerAndNext(q.id, arr)}
              />
            )}
          </div>
        ) : (
          <div style={css.card} data-testid="popup-only-phase">
            <h2 style={{ ...css.h, color: "#10b981" }}>✓ Standard questions complete</h2>
            <p style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 2 }}>
              Assessment is continuing. Please stay on this screen — short writing prompts will appear and disappear within 12 seconds each. Stay focused.
            </p>
            <div style={{ marginTop: 14, padding: 12, background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: 8, fontSize: 12, color: "#d4af37", textAlign: "center" }} data-testid="popup-only-progress">
              Writing prompts: {popupsAnsweredCount} / {popupsTotal}
            </div>
          </div>
        )}

        <div style={{ fontSize: 11, color: "#9a9a9f", textAlign: "center", marginTop: 6 }}>
          Take your time. No copy/paste restriction in this stage. · خذ وقتك، لا قيود على النسخ أو اللصق في هذه المرحلة.
        </div>
      </div>

      {/* English-writing popup */}
      {popup && (
        <PopupWriting popup={popup} secondsLeft={popupSecs} onSubmit={submitPopup} />
      )}
    </div>
  );
}

// ──────────── OrderQuestion: drag-free ordering via click sequence ────────────
function OrderQuestion({ q, draft, setDraft, onSubmit }) {
  const picked = new Set(draft);
  const remaining = q.steps.filter((s) => !picked.has(s.k));
  const css = {
    box: { background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 10, padding: 12, marginBottom: 12 },
    chip: (active) => ({ padding: "8px 12px", margin: "4px 6px 4px 0", background: active ? "#d4af37" : "rgba(212,175,55,0.1)", color: active ? "#000" : "#fff", border: "1px solid rgba(212,175,55,0.4)", borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 700 }),
    btn: { padding: "10px 18px", background: "#10b981", color: "#fff", border: "none", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontSize: 13 },
  };
  return (
    <div>
      <div style={css.box}>
        <div style={{ fontSize: 11, color: "#9a9a9f", marginBottom: 6 }}>الترتيب الذي اخترته · Your order:</div>
        <div data-testid={`order-picked-${q.id}`}>
          {draft.length === 0
            ? <span style={{ fontSize: 12, color: "#6b7280" }}>اضغط على الخطوات أدناه بالترتيب · Click the steps below in order</span>
            : draft.map((k, i) => {
                const stp = q.steps.find((x) => x.k === k);
                return <span key={k} style={css.chip(true)}>{i + 1}. {stp?.label_ar || k}{stp?.label_en ? ` · ${stp.label_en}` : ""}</span>;
              })
          }
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#9a9a9f", marginBottom: 6 }}>الخطوات المتاحة · Available steps:</div>
      <div>
        {remaining.map((stp) => (
          <button key={stp.k} onClick={() => setDraft([...draft, stp.k])} style={css.chip(false)} data-testid={`order-step-${q.id}-${stp.k}`}>
            {stp.label_ar}{stp.label_en ? ` · ${stp.label_en}` : ""}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={() => setDraft([])} disabled={!draft.length} style={{ padding: "8px 14px", background: "transparent", border: "1px solid #6b7280", color: "#9a9a9f", borderRadius: 8, cursor: draft.length ? "pointer" : "not-allowed", fontSize: 12 }} data-testid={`order-reset-${q.id}`}>إعادة · Reset</button>
        <button onClick={() => onSubmit(draft)} disabled={draft.length !== q.steps.length} style={{ ...css.btn, opacity: draft.length === q.steps.length ? 1 : 0.4, cursor: draft.length === q.steps.length ? "pointer" : "not-allowed" }} data-testid={`order-submit-${q.id}`}>تأكيد الترتيب · Confirm</button>
      </div>
    </div>
  );
}

// ──────────── PopupWriting: English-only writing, 12s timer ────────────
function PopupWriting({ popup, secondsLeft, onSubmit }) {
  const [text, setText] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }} data-testid="popup-writing">
      <div style={{ background: "#1a1a25", border: "2px solid #d4af37", borderRadius: 14, padding: 24, maxWidth: 460, width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, alignItems: "center" }}>
          <span style={{ color: "#d4af37", fontWeight: 800, fontSize: 13 }}>Quick translation · ترجمة سريعة</span>
          <span style={{ color: secondsLeft < 4 ? "#dc2626" : "#d4af37", fontWeight: 900, fontSize: 22, fontVariantNumeric: "tabular-nums" }} data-testid="popup-timer">{secondsLeft}s</span>
        </div>
        <div style={{ direction: "rtl", fontSize: 16, marginBottom: 6, color: "#cbd5e1" }}>{popup.prompt_ar}</div>
        {popup.prompt_en && <div style={{ direction: "ltr", fontSize: 13, marginBottom: 12, color: "#9ca3af" }}>{popup.prompt_en}</div>}
        <input
          ref={inputRef}
          type="text"
          autoFocus
          placeholder="Type in English · اكتب بالإنجليزية"
          onKeyDown={(e) => { if (e.key === "Enter") onSubmit(text); }}
          onChange={(e) => setText(e.target.value)}
          value={text}
          style={{ width: "100%", padding: "12px 14px", background: "#0a0a0f", border: "1px solid #d4af37", borderRadius: 8, color: "#fff", fontSize: 16, outline: "none", direction: "ltr" }}
          data-testid="popup-input"
        />
        <button onClick={() => onSubmit(text)} style={{ width: "100%", marginTop: 12, padding: "11px 22px", background: "#10b981", color: "#fff", border: "none", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontSize: 13 }} data-testid="popup-submit">Submit · إرسال</button>
      </div>
    </div>
  );
}
